import { useContext, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { ROUTES } from '@app/router';
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, useLocation } from 'react-router';
import {
  fetchDisabledFeatures,
  getAndSetIsSideMenuPinned,
  setScreenSize,
  fetchTableSettings,
  getAndSetIsInfoPaneExpanded,
  getAlertTags,
} from 'redux_store';
import { qhsDebug } from '@shared/lib/qhsDebug';
import useNotifier from '@app/providers/useNotifier';
import { changeCurrentAccountForAlertsNotifications } from 'redux_store/middleware/alertsMiddleware';
import { UserContext } from '@app/providers';
import ProtectedRoute from './widgets/ProtectedRoute';
import PublicRoute from './widgets/PublicRoute';
import EnvTopBar from './widgets/EnvTopBar';
import { SessionIdle } from '@features/session-idle';
import { ToastContainer } from 'react-toastify';
import NotFound from 'pages/not-found';
import { AppLoader } from '@widgets/app-loader';

// Theme-aware app surface. The legacy gray3 ::before pseudo + silver
// logo background image were removed: they were not theme-aware (always
// light), and the silver mark flashed during reloads. The dashboard
// AppShell paints its own bg over this anyway.
const Main = styled('main')({
  height: '100vh',
  position: 'relative',
  backgroundColor: 'var(--qhs-bg)',
});

const App = () => {
  useNotifier();
  const dispatch = useDispatch();
  const location = useLocation();
  const { isLogin, user, metadata } = useSelector(state => state.user);
  const { changeAccountLoading } = useContext(UserContext);
  const [showContent, setShowContent] = useState(false);
  const [isLoaderHiding, setIsLoaderHiding] = useState(false);
  const [showLoader, setShowLoader] = useState(true);

  const isLoginPage = location.pathname.startsWith('/login');

  useEffect(() => {
    if (!changeAccountLoading) {
      setShowContent(true);

      const renderDelay = isLoginPage ? 100 : 300;
      const animationDuration = isLoginPage ? 150 : 400;

      const hideTimer = setTimeout(() => {
        setIsLoaderHiding(true);

        const removeTimer = setTimeout(() => {
          setShowLoader(false);
        }, animationDuration);

        return () => clearTimeout(removeTimer);
      }, renderDelay);

      return () => clearTimeout(hideTimer);
    } else {
      setShowContent(false);
      setIsLoaderHiding(false);
      setShowLoader(true);
    }
  }, [changeAccountLoading, isLoginPage]);

  useEffect(() => {
    dispatch(getAndSetIsSideMenuPinned());
    dispatch(getAndSetIsInfoPaneExpanded());
    dispatch(fetchDisabledFeatures());
    if (isLogin) {
      dispatch(getAlertTags());
    }
  }, []);

  useEffect(() => {
    qhsDebug('App:isLoginEffect:fire', {
      isLogin,
      hasUser: !!user,
      conditionPasses: !!(isLogin && !user),
    });
    // The alerts hub is not started here: it starts itself after the
    // first of these requests succeeds (see alertsMiddleware), so it
    // never connects with a cookie the same request is about to renew.
    if (isLogin && !user) {
      dispatch(getAlertTags());
      dispatch(fetchTableSettings());
    }
  }, [isLogin]);

  useEffect(() => {
    if (metadata?.currentAccount?.uuid) {
      dispatch(changeCurrentAccountForAlertsNotifications(metadata?.currentAccount?.uuid));
    }
  }, [metadata?.currentAccount?.uuid]);

  useEffect(() => {
    const updateScreenSize = () => {
      dispatch(
        setScreenSize({
          height: window.innerHeight,
          width: window.innerWidth,
        })
      );
    };

    window.addEventListener('resize', updateScreenSize);
    return () => {
      window.removeEventListener('resize', updateScreenSize);
    };
  }, []);

  return (
    <>
      <ToastContainer />
      <SessionIdle />
      <Main>
        <EnvTopBar />
        {showContent && (
          <Routes>
            {ROUTES.map(route => {
              const { additional } = route;
              return additional.protected ? (
                <Route
                  key={route.path}
                  exact
                  path={route.path}
                  element={<ProtectedRoute {...route} />}
                />
              ) : (
                <Route
                  key={route.path}
                  exact
                  path={route.path}
                  element={<PublicRoute {...route} />}
                />
              );
            })}
            {/* Catch-all — funnel unknown URLs through ProtectedRoute so
                the user keeps the AppShell chrome (sidebar + topbar) on
                404 if logged in, or gets redirected to /login if not. */}
            <Route
              path="*"
              element={
                <ProtectedRoute path="*" additional={{ component: NotFound, protected: true }} />
              }
            />
          </Routes>
        )}

        {showLoader && !isLoginPage && <AppLoader isHiding={isLoaderHiding} />}
      </Main>
    </>
  );
};

export default App;
